export const GUIDE_CATEGORIES = [
  'Seasonal Produce',
  'Shopping Tips',
  'For Farmers',
  'Local Food',
  'Payments',
];

export const GUIDES = [
  {
    slug: 'seasonal-produce-guide-province-1',
    title: 'Seasonal Produce Guide for Province 1, Nepal',
    description:
      'A month-by-month guide to fresh vegetables and fruits in Morang, Sunsari, and Jhapa — know what is in season and buy at peak freshness.',
    category: 'Seasonal Produce',
    publishedAt: '2026-03-15',
    readTimeMinutes: 8,
    sections: [
      {
        h: 'Why seasonality matters in eastern Nepal',
        p: [
          'Province 1 benefits from fertile Terai plains and access to hill produce from nearby districts. Seasonal eating is not just a trend here — it reflects what local farms actually harvest each month. Tomatoes, cauliflower, and mustard greens in winter; mangoes, litchi, and jackfruit in summer; rice and maize at harvest time — each season brings distinct flavours and better prices when supply is abundant.',
          'Shopping seasonally on Taja Bazzar means you buy from farmers listing what they picked recently, often the same day. Produce in season travels shorter distances, costs less, and retains more nutrients than out-of-season items trucked from distant wholesale markets.',
        ],
      },
      {
        h: 'Winter (Poush–Falgun / December–February)',
        p: [
          'Winter is peak season for leafy greens and root vegetables across the Terai. Farmers in Biratnagar and Itahari commonly harvest spinach (palak), mustard greens (rayo ko saag), fenugreek leaves, cabbage, cauliflower, broccoli, carrots, radish, and potatoes. Citrus fruits including oranges and sweet lime appear from orchards in Jhapa and eastern Morang.',
          'This is an excellent time to stock up on greens for daily cooking. Prices are typically lowest in Magh when supply peaks. Look for firm cauliflower heads without brown spots and crisp spinach bunches with deep green leaves.',
        ],
      },
      {
        h: 'Spring (Chaitra–Jestha / March–May)',
        p: [
          'Temperatures rise and the harvest palette shifts. Expect tomatoes, cucumbers, bottle gourd (lauka), bitter gourd (karela), okra, beans, and early mangoes by Baisakh. Strawberries and bananas are widely available from local growers in Sunsari.',
          'Spring transitions quickly into pre-monsoon heat — shop early in the day when farmers list morning harvests. Tomatoes and cucumbers are versatile staples; buying in bulk during peak weeks saves money for chutney and pickle making.',
        ],
      },
      {
        h: 'Monsoon (Asar–Bhadra / June–August)',
        p: [
          'Heavy rains challenge transport but bring monsoon delicacies: fern fiddleheads (niguro), bamboo shoots, wild mushrooms in hill-adjacent areas, and robust gourds that thrive in wet conditions. Leafy saag varieties continue; snails and fresh fish appear in some local markets though availability varies.',
          'During monsoon, check farmer messages for delivery delays and prioritise produce with longer shelf life — pumpkins, gourds, and root vegetables handle humidity better than delicate greens. Taja Bazzar messaging helps coordinate pickup when roads are affected.',
        ],
      },
      {
        h: 'Autumn and festival season (Ashwin–Mangsir / September–November)',
        p: [
          'Dashain and Tihar drive demand for fruits, nuts, and premium vegetables. Apples from Dhankuta and Ilam reach eastern markets; local pomegranates, guavas, and persimmons ripen. Post-monsoon rice harvest brings fresh stock (nayā chamal) prized for festivals.',
          'Plan festival shopping a week ahead — verified farmers on Taja Bazzar often accept pre-orders for bulk fruit boxes and vegetable assortments. Supporting local growers during festival season directly benefits farming families in your district.',
        ],
      },
    ],
  },
  {
    slug: 'benefits-of-buying-direct-from-farmers',
    title: '7 Benefits of Buying Direct From Local Farmers',
    description:
      'Skip the middleman — discover why direct farm-to-consumer shopping in eastern Nepal means fresher food, fairer prices, and stronger communities.',
    category: 'Shopping Tips',
    publishedAt: '2026-02-20',
    readTimeMinutes: 6,
    sections: [
      {
        h: 'Fresher produce, fewer storage days',
        p: [
          'Traditional supply chains move produce from farm to collection centre, wholesaler, retailer, and finally your kitchen — often adding three to seven days and multiple handling steps. Direct purchase shortens that chain dramatically. On Taja Bazzar, many items are harvested hours before you order.',
          'Fresher vegetables retain more vitamins and better texture. A tomato picked yesterday tastes fundamentally different from one that sat in a warehouse. Your dal and sabzi benefit from that difference every day.',
        ],
      },
      {
        h: 'Fairer prices for farmers and consumers',
        p: [
          'Middlemen capture a significant share of the final retail price while taking minimal risk. When you buy direct, farmers keep a larger portion of what you pay. That enables them to invest in seeds, irrigation, and organic practices without raising prices unsustainably.',
          'Consumers often pay less than supermarket equivalents for comparable freshness because overhead costs are lower. Transparent listing prices on Taja Bazzar let you compare farmers in your district before buying.',
        ],
      },
      {
        h: 'Know where your food comes from',
        p: [
          'Every product on Taja Bazzar links to a farmer profile with location, specialties, and reviews. You can ask whether pesticides were used, how chickens were raised, or when rice was milled — and get answers from the person who actually produced the food.',
          'This transparency is especially valuable for families prioritising organic produce, allergen awareness, or supporting specific farming practices in Morang, Sunsari, and Jhapa.',
        ],
      },
      {
        h: 'Supporting the local economy',
        p: [
          'Money spent with local farmers circulates within your community — paying school fees, buying feed, hiring seasonal workers. Import-heavy shopping sends value out of the region. Choosing local producers strengthens the agricultural economy that sustains Province 1.',
          'Taja Bazzar was built specifically to keep that economic value in eastern Nepal by connecting Terai farmers directly with nearby consumers.',
        ],
      },
      {
        h: 'Reduced packaging and transport emissions',
        p: [
          'Farm-direct orders typically use simpler packaging — reused crates, paper bags, or minimal plastic compared to individually wrapped supermarket items. Shorter transport distances from Biratnagar to Itahari mean lower fuel use per kilogram of food delivered.',
          'While not every order is zero-waste, conscious local shopping moves in that direction compared to national supply chains.',
        ],
      },
    ],
  },
  {
    slug: 'how-to-store-fresh-vegetables-nepal',
    title: 'How to Store Fresh Vegetables in Nepal\'s Climate',
    description:
      'Practical storage tips for Terai heat and monsoon humidity — keep your farm-fresh vegetables lasting longer after delivery.',
    category: 'Local Food',
    publishedAt: '2026-01-10',
    readTimeMinutes: 7,
    sections: [
      {
        h: 'Understanding Nepal\'s storage challenges',
        p: [
          'Eastern Nepal\'s Terai climate combines high summer temperatures (often 35°C+) with monsoon humidity above 80%. These conditions accelerate spoilage for leafy greens and soft fruits. Refrigerators help but are not universal; many households rely on cool corners, clay pots (mitti ko ghada), and daily market shopping.',
          'The goal is to match storage method to each vegetable type rather than treating everything the same.',
        ],
      },
      {
        h: 'Leafy greens (spinach, saag, coriander)',
        p: [
          'Wrap unwashed bunches loosely in a damp cloth or paper towel and store in the vegetable compartment of your refrigerator. Use within two to three days. In homes without refrigeration, stand stems in a jar of water like flowers and keep away from direct sunlight — change water daily.',
          'Never store wet leaves in sealed plastic bags; trapped moisture causes rot within hours in hot weather.',
        ],
      },
      {
        h: 'Root vegetables and gourds',
        p: [
          'Potatoes, onions, and garlic prefer cool, dark, ventilated spaces — not the refrigerator. A woven basket under the kitchen counter works well. Keep onions separate from potatoes; onions release gases that sprout potatoes faster.',
          'Bottle gourd, pumpkin, and hard squash last one to three weeks in a shaded room. Once cut, wrap in cling film and refrigerate the unused portion.',
        ],
      },
      {
        h: 'Tomatoes and fruits',
        p: [
          'Ripen tomatoes at room temperature stem-side down. Refrigeration dulls flavour but extends life once fully ripe — a trade-off during heat waves. Mangoes and bananas should never be refrigerated until ripe; cold stops the ripening process and causes blackening.',
          'After buying from Taja Bazzar farmers, ask when items were harvested. Same-day greens need immediate attention; root vegetables bought fresh tolerate several days.',
        ],
      },
      {
        h: 'When to shop more frequently',
        p: [
          'During peak summer and monsoon, plan smaller, more frequent orders rather than weekly bulk purchases. Taja Bazzar\'s Nearby map makes it easy to find farmers close enough for quick pickup. Less waste means better value and fresher meals every day.',
        ],
      },
    ],
  },
  {
    slug: 'understanding-organic-farming-nepal',
    title: 'Understanding Organic Farming in Eastern Nepal',
    description:
      'What "organic" really means for local farms in Morang, Sunsari, and Jhapa — and how to shop with confidence on Taja Bazzar.',
    category: 'Local Food',
    publishedAt: '2026-04-05',
    readTimeMinutes: 7,
    sections: [
      {
        h: 'Organic farming in the Terai context',
        p: [
          'Nepal has a growing organic movement, particularly in vegetable belts around Biratnagar, Damak, and Birtamod. Many small farmers follow traditional low-chemical practices — crop rotation, compost from livestock, neem-based pest control — without formal certification because certification costs exceed their annual revenue.',
          'On Taja Bazzar, farmers describe their practices in product listings and profiles. "Organic" may mean certified, pesticide-free, or traditionally grown depending on the seller. Read descriptions carefully and message farmers with specific questions.',
        ],
      },
      {
        h: 'Questions to ask your farmer',
        p: [
          'Useful questions include: Do you use chemical pesticides or fertilisers? How do you manage pests on leafy crops? Is this produce from your own farm or sourced elsewhere? When was it last sprayed (if applicable)?',
          'Honest farmers welcome these questions. Vague answers or unwillingness to discuss practices may warrant choosing another seller. Reviews from other consumers also highlight consistent quality.',
        ],
      },
      {
        h: 'Certification vs. trust-based organic',
        p: [
          'Nepal Organic Certification Body (NOCB) and international standards exist but cover a fraction of actual organic producers. Trust-based relationships — repeat purchases from verified farmers with transparent practices — often serve consumers better than uncertified claims on packaging at supermarkets.',
          'Taja Bazzar verification confirms identity and location, not organic status. Combine verified profiles with detailed listings and customer reviews for the most reliable picture.',
        ],
      },
      {
        h: 'Price expectations',
        p: [
          'Genuinely organic or pesticide-free produce typically costs 15–40% more than conventional equivalents due to lower yields and labour-intensive pest management. Suspiciously cheap "organic" listings deserve scrutiny. Fair pricing supports farmers who invest in sustainable methods without cutting corners.',
        ],
      },
    ],
  },
  {
    slug: 'esewa-khalti-payments-guide',
    title: 'How to Pay With eSewa and Khalti on Taja Bazzar',
    description:
      'A clear guide to online payments and cash on delivery when ordering fresh produce from local farmers.',
    category: 'Payments',
    publishedAt: '2026-02-01',
    readTimeMinutes: 5,
    sections: [
      {
        h: 'Payment options available',
        p: [
          'Taja Bazzar supports eSewa, Khalti, and cash on delivery (COD) depending on the order and farmer settings. Online payment confirms your order immediately and reduces no-show pickups. COD suits customers who prefer paying when produce arrives.',
          'All online payments are processed through established Nepali payment gateways — Taja Bazzar does not store your full wallet credentials.',
        ],
      },
      {
        h: 'Paying with eSewa',
        p: [
          'At checkout, select eSewa as your payment method. You will be redirected to complete payment using your eSewa ID and PIN or OTP. Ensure your eSewa wallet has sufficient balance or linked bank account before confirming.',
          'After successful payment, you return to Taja Bazzar with an order confirmation. The farmer receives notification to prepare your items. Keep your transaction ID until delivery is complete.',
        ],
      },
      {
        h: 'Paying with Khalti',
        p: [
          'Khalti works similarly — choose Khalti at checkout, authenticate in the Khalti app or web flow, and confirm. Khalti users with linked bank accounts can pay directly without pre-loading wallet balance.',
          'If payment fails, your order is not confirmed. Check your Khalti transaction history before retrying to avoid duplicate charges.',
        ],
      },
      {
        h: 'Cash on delivery tips',
        p: [
          'For COD orders, have exact change when possible — many farmers operate on tight margins. Confirm delivery address and phone number at checkout so farmers can coordinate arrival time.',
          'If you cannot receive delivery, message the farmer early to reschedule rather than refusing delivery without notice.',
        ],
      },
      {
        h: 'Refunds and disputes',
        p: [
          'If an order issue requires refund, contact the farmer first through messages. For unresolved payment problems, email contact@tajabazzar.me with your order ID and payment reference. Refund processing times depend on the payment provider and typically take three to seven business days.',
        ],
      },
    ],
  },
  {
    slug: 'farmer-verification-taja-bazzar',
    title: 'How Farmer Verification Works on Taja Bazzar',
    description:
      'What the verified badge means, how farmers qualify, and why it matters for safe local food shopping.',
    category: 'For Farmers',
    publishedAt: '2026-03-01',
    readTimeMinutes: 5,
    sections: [
      {
        h: 'The verified farmer badge',
        p: [
          'Verified farmers on Taja Bazzar have completed our identity and profile review. The green checkmark appears on their public profile, in search results, and on product listings. It indicates our team confirmed basic account authenticity — not that every product claim has been laboratory tested.',
          'Consumers can filter the Farmers directory to show verified sellers only, which is useful for first-time buyers building trust with local producers.',
        ],
      },
      {
        h: 'What we review',
        p: [
          'Verification reviews include profile completeness, accurate farm location within Morang/Sunsari/Jhapa, reachable contact information, and at least one genuine product listing with original photos. We look for signs of reseller fraud — stock photos, inconsistent locations, or duplicate accounts.',
        ],
      },
      {
        h: 'Timeline and appeals',
        p: [
          'New farmers typically receive verification review within five to seven business days after completing their profile. Delays occur during festival seasons or if additional information is needed. Farmers can contact support at contact@tajabazzar.me to check status.',
          'If verification is denied, we explain the reason when possible — incomplete profile, location mismatch, or policy concerns. Fix the issue and request re-review through the contact form.',
        ],
      },
    ],
  },
  {
    slug: 'eastern-nepal-farming-traditions',
    title: 'Farming Traditions of Eastern Nepal\'s Terai',
    description:
      'Explore the agricultural heritage of Morang, Sunsari, and Jhapa — rice paddies, vegetable belts, and festival harvest customs.',
    category: 'Local Food',
    publishedAt: '2026-05-12',
    readTimeMinutes: 9,
    sections: [
      {
        h: 'The agricultural heartland of Province 1',
        p: [
          'Morang, Sunsari, and Jhapa form the economic backbone of eastern Nepal\'s Terai agriculture. Irrigated by Koshi and Mechi river systems and fed by monsoon rains, these districts produce rice, maize, jute, tea (in Jhapa\'s hills), sugarcane, and a vast array of vegetables for domestic and cross-border markets.',
          'Biratnagar anchors Morang\'s industrial and agricultural trade. Itahari in Sunsari serves as a logistics hub. Birtamod and Damak in Jhapa connect farmers to consumers in Nepal and India. Taja Bazzar digitises relationships that historically depended on haat bazaars and middlemen networks.',
        ],
      },
      {
        h: 'Rice culture and Dashain nayā chamal',
        p: [
          'Rice is more than food — it structures the farming calendar. Planting in Asar (national Paddy Day traditions), tending through monsoon, harvesting in Kartik/Mangsir, and eating nayā chamal (new rice) during Dashain connects families to the land. Many urban consumers in Biratnagar still seek specific rice varieties from home villages.',
          'Farmers listing rice, flour, or beaten rice (chiura) on Taja Bazzar often highlight variety names — Mansuli, Sona Mansuli, or local landraces — that matter to culturally informed buyers.',
        ],
      },
      {
        h: 'Vegetable belts and migrant labour',
        p: [
          'Commercial vegetable production concentrates in peri-urban areas around Biratnagar, Inaruwa, and Damak. Tunnel farming (plastic greenhouses) extends seasons for tomatoes, cucumbers, and capsicum. Labour from hill districts and across the border supports peak harvest periods.',
          'Understanding this context helps consumers appreciate pricing fluctuations — a sudden heatwave or border delay affects supply and prices within days on local markets.',
        ],
      },
      {
        h: 'Preserving tradition through direct sales',
        p: [
          'Direct-to-consumer platforms help younger farmers stay on the land rather than migrating for labour. When a Biratnagar family buys cauliflower directly from a Sunsari farmer, they sustain practices passed through generations — seed saving, composting, intercropping — that industrial supply chains ignore.',
          'Taja Bazzar aims to be part of that preservation: technology serving tradition, not replacing it.',
        ],
      },
    ],
  },
  {
    slug: 'smart-shopping-local-marketplace',
    title: 'Smart Shopping Tips for Taja Bazzar',
    description:
      'Get the most from your local marketplace — filters, reviews, Nearby map, and messaging farmers before you buy.',
    category: 'Shopping Tips',
    publishedAt: '2026-04-18',
    readTimeMinutes: 6,
    sections: [
      {
        h: 'Start with the Nearby map',
        p: [
          'The Nearby page shows farmers and products on a map centred on your location. This is ideal for finding the closest fresh produce and minimising delivery time. Enable location permission for accurate results, or search manually for Biratnagar, Itahari, or Birtamod.',
          'Closer farmers often offer pickup options that save delivery fees and guarantee maximum freshness.',
        ],
      },
      {
        h: 'Use filters and sort wisely',
        p: [
          'On the Marketplace page, filter by category, price range, and district. Sort by rating to surface trusted sellers or by newest to see recent harvests. Search by product name in Nepali or English — many farmers list items in both languages.',
        ],
      },
      {
        h: 'Read profiles and reviews',
        p: [
          'Before ordering from a new farmer, read their bio, specialties, and customer reviews. Look for consistent praise about freshness, communication, and accurate weights. One negative review among many positives is normal; patterns of complaints about stale produce or no-shows are red flags.',
        ],
      },
      {
        h: 'Message before bulk orders',
        p: [
          'For festival shopping, event catering, or weekly box orders, message the farmer first to confirm availability and bulk pricing. Many sellers appreciate advance notice and may offer discounts for regular customers.',
          'Clear communication prevents disappointment and builds relationships that make local shopping more rewarding over time.',
        ],
      },
      {
        h: 'Leave honest reviews',
        p: [
          'After delivery, leave a review describing quality and service. Reviews help other consumers and reward good farmers with visibility. Constructive feedback also helps sellers improve — the community grows stronger when everyone participates.',
        ],
      },
    ],
  },
];

export function getGuideBySlug(slug) {
  return GUIDES.find((g) => g.slug === slug) || null;
}

export function getGuidesByCategory(category) {
  return GUIDES.filter((g) => g.category === category);
}
